import React, { useRef, useState } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";

import { Icon } from "@iconify/react";
import { addOtherReport } from "../../../api/collection";
import { toast } from "sonner";

const reportTypeOptions = [
  { label: "ECG", value: "ECG" },
  { label: "Blood Test", value: "BLOOD_TEST" },
  { label: "X-Ray", value: "XRAY" },
  { label: "Other", value: "OTHER" },

  { label: "IP Admission Note", value: "IP_ADMISSION_NOTE" },
  { label: "Patient Case Record", value: "PATIENT_CASE_RECORD" },
  { label: "Patient Assessment", value: "PATIENT_ASSESSMENT" },
  { label: "Progress Note", value: "PROGRESS_NOTE" },
  { label: "Treatment Chart", value: "TREATMENT_CHART" },
  { label: "Medical Prescription", value: "MEDICAL_PRESCRIPTION" },
  { label: "Referral Letter", value: "REFERRAL_LETTER" },
  { label: "Handover / Transfer Form", value: "HANDOVER_TRANSFER_FORM" },
  { label: "Discharge / Closure Summary", value: "DISCHARGE_CLOSURE_SUMMARY" },
];

const UploadReportModal = ({
  open,
  onClose,
  patientId,
  onUploadSuccess,
}) => {
  const [reportType, setReportType] = useState("");
  const [customReportType, setCustomReportType] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef(null);

  const resetState = () => {
    setReportType("");
    setCustomReportType("");
    setFile(null);
  };

  const closeModal = () => {
    if (!uploading) {
      resetState();
      onClose?.();
    }
  };

  const handleFileSelected = (f) => {
    if (!f) {
      setFile(null);
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
    ];

    if (!allowedTypes.includes(f.type)) {
      toast.error("Only PDF, PNG or JPG allowed");
      return;
    }

    const maxSize = 10 * 1024 * 1024;

    if (f.size > maxSize) {
      toast.error("File must be less than 10MB");
      return;
    }

    setFile(f);
  };

  const handleFileInputChange = (e) => {
    const selected = e.target.files?.[0];
    handleFileSelected(selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();

    const dropped = e.dataTransfer.files?.[0];

    handleFileSelected(dropped);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleUpload = async () => {
    try {
      if (!patientId) {
        toast.error("Missing patient id");
        return;
      }

      if (!reportType) {
        toast.error("Please select report type");
        return;
      }

      let uploadKey = reportType;

      if (reportType === "OTHER") {
        if (!customReportType.trim()) {
          toast.error("Please enter a report type");
          return;
        }

        uploadKey = customReportType.trim();
      }

      if (!file) {
        toast.error("Please select file");
        return;
      }

      setUploading(true);

      const form = new FormData();

      form.append("userId", patientId);

      form.append(uploadKey, file);

      const res = await addOtherReport(form);

      if (res?.success) {
        toast.success(
          res?.message || "Report uploaded successfully"
        );

        onUploadSuccess?.(res);

        closeModal();
      } else {
        toast.error(res?.message || "Upload failed");
      }
    } catch (err) {
      console.error(err);

      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={closeModal}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "visible",
          p: 0,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 4,
          pt: 4,
          pb: 2,
          position: "relative",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Upload New Report
        </Typography>

        <IconButton
          onClick={closeModal}
          size="large"
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            width: 44,
            height: 44,
          }}
        >
          <Icon icon="ic:round-close" width="22" height="22" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 4, pt: 0, pb: 3 }}>
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: "3px dashed",
            borderColor: "#6C5CE7",
            borderRadius: "12px",
            px: 4,
            py: 5,
            textAlign: "center",
            mb: 3,
            bgcolor: "var(--app-surface-2)",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <Icon
            icon="heroicons-outline:arrow-up-tray"
            width="36"
            height="36"
            style={{ color: "#6C5CE7" }}
          />

          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            Upload File Here
          </Typography>

          <Typography
            sx={{
              color: "var(--app-text-muted)",
              fontSize: 13,
            }}
          >
            Click to choose or drag and drop a file
            (PDF, PNG, JPG). Max 10MB.
          </Typography>

          {file && (
            <Typography sx={{ mt: 1, fontSize: 14 }}>
              <strong>Selected:</strong> {file.name}
            </Typography>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,image/png,image/jpeg"
            style={{ display: "none" }}
            onChange={handleFileInputChange}
          />
        </Box>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="report-type-label">
            Select Report Type
          </InputLabel>

          <Select
            labelId="report-type-label"
            value={reportType}
            label="Select Report Type"
            onChange={(e) => setReportType(e.target.value)}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: 2,
                },
              },
            }}
            sx={{
              borderRadius: 2,
              height: 56,
            }}
          >
            {reportTypeOptions.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {reportType === "OTHER" && (
          <TextField
            fullWidth
            label="Type report name"
            placeholder="e.g. Lipid Profile"
            value={customReportType}
            onChange={(e) =>
              setCustomReportType(e.target.value)
            }
            inputProps={{ maxLength: 60 }}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          pb: 4,
          pt: 0,
          display: "block",
        }}
      >
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={
            uploading ||
            !file ||
            !reportType ||
            (reportType === "OTHER" &&
              !customReportType.trim())
          }
          fullWidth
          size="large"
          sx={{
            backgroundColor: "#5751E9",
            textTransform: "none",
            borderRadius: 2,
            py: 1.6,
            fontWeight: 600,
          }}
        >
          {uploading ? "Uploading..." : "Save Report"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadReportModal;